import { Badge } from "@/components/ui/badge";
import { Signal, SignalHigh, SignalLow, SignalMedium } from "lucide-react";
import { cn } from "@/lib/utils";

interface GPSDiagnosticBadgeProps {
  pointsCollected: number;
  pointsRejected: number;
  currentAccuracy?: number;
  isWarmingUp: boolean;
}

export const GPSDiagnosticBadge = ({
  pointsCollected,
  pointsRejected,
  currentAccuracy,
  isWarmingUp
}: GPSDiagnosticBadgeProps) => {
  const totalPoints = pointsCollected + pointsRejected;
  const rejectionRate = totalPoints > 0 ? (pointsRejected / totalPoints) * 100 : 0;
  
  const getAccuracyColor = () => {
    if (!currentAccuracy) return "bg-muted";
    if (currentAccuracy < 20) return "bg-green-500";
    if (currentAccuracy < 40) return "bg-yellow-500";
    if (currentAccuracy < 60) return "bg-orange-500";
    return "bg-red-500";
  };
  
  const getAccuracyIcon = () => {
    if (!currentAccuracy) return <Signal className="w-3 h-3" />;
    if (currentAccuracy < 20) return <SignalHigh className="w-3 h-3" />;
    if (currentAccuracy < 60) return <SignalMedium className="w-3 h-3" />;
    return <SignalLow className="w-3 h-3" />;
  };

  return (
    <div className="flex gap-2 flex-wrap">
      {/* GPS Accuracy Badge */}
      <Badge 
        variant="secondary" 
        className={cn(
          "flex items-center gap-1",
          getAccuracyColor(),
          "text-white"
        )}
      >
        {getAccuracyIcon()}
        {currentAccuracy ? `${Math.round(currentAccuracy)}m` : "N/A"}
      </Badge>

      {/* Points Collected Badge */}
      <Badge variant="secondary" className="bg-blue-500 text-white">
        {pointsCollected} pontos
      </Badge>

      {/* Warmup Indicator */}
      {isWarmingUp && (
        <Badge variant="secondary" className="bg-purple-500 text-white animate-pulse">
          Aquecendo GPS...
        </Badge>
      )}

      {/* High Rejection Rate Warning */}
      {rejectionRate > 30 && pointsCollected > 5 && (
        <Badge variant="destructive" className="animate-pulse">
          ⚠️ {Math.round(rejectionRate)}% rejeitados
        </Badge>
      )}
    </div>
  );
};
